// EventsServices.js
import { useState, useCallback } from "react";
import API from "../../api/apiAdmin";
import { useNotification } from "../../context/NotificationContext";

export function useEvents() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { showNotification } = useNotification();

    const fetchAllEvents = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await API.get("/admin/events/");
            return res.data;
        } catch (err) {
            const msg = err.response?.data?.message || "Failed to fetch events.";
            setError(msg);
            return [];
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchEventById = useCallback(async (id) => {
        setLoading(true);
        setError(null);
        try {
            const res = await API.get(`/admin/events/${id}`);
            return res.data;
        } catch (err) {
            const msg = err.response?.data?.message || "Failed to fetch event details.";
            setError(msg);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    const createEvent = useCallback(async (data) => {
        setLoading(true);
        setError(null);
        try {
            const res = await API.post("/admin/events/", data);
            if (res.data.status === "success") {
                showNotification({
                    type: "success",
                    title: "Event Created",
                    message: res.data.message,
                });
            }
            return res.data;
        } catch (err) {
            let msg = err.response?.data?.message || "Failed to create event.";
            if (err.response?.data?.errors && Array.isArray(err.response.data.errors)) {
                msg += ": " + err.response.data.errors.join(", ");
            }

            setError(msg);
            showNotification({
                type: "failed",
                title: "Error",
                message: msg,
            });
            return { status: "failed", message: msg, errors: err.response?.data?.errors };
        } finally {
            setLoading(false);
        }
    }, [showNotification]);

    const updateEvent = useCallback(async (id, data) => {
        setLoading(true);
        setError(null);
        try {
            const res = await API.put(`/admin/events/${id}`, data);
            if (res.data.status === "success") {
                showNotification({
                    type: "success",
                    title: "Event Updated",
                    message: res.data.message,
                });
            }
            return res.data;
        } catch (err) {
            let msg = err.response?.data?.message || "Failed to update event.";
            if (err.response?.data?.errors && Array.isArray(err.response.data.errors)) {
                msg += ": " + err.response.data.errors.join(", ");
            }

            setError(msg);
            showNotification({
                type: "failed",
                title: "Error",
                message: msg,
            });
            return { status: "failed", message: msg, errors: err.response?.data?.errors };
        } finally {
            setLoading(false);
        }
    }, [showNotification]);

    const deleteEvent = useCallback(async (id) => {
        setLoading(true);
        setError(null);
        try {
            const res = await API.delete(`/admin/events/${id}`);
            if (res.data.status === "success") {
                showNotification({
                    type: "success",
                    title: "Event Deleted",
                    message: res.data.message,
                });
            }
            return res.data;
        } catch (err) {
            const msg = err.response?.data?.message || "Failed to delete event.";
            setError(msg);
            showNotification({
                type: "failed",
                title: "Error",
                message: msg,
            });
            return { status: "failed", message: msg };
        } finally {
            setLoading(false);
        }
    }, [showNotification]);

    return {
        loading,
        error,
        fetchAllEvents,
        fetchEventById,
        createEvent,
        updateEvent,
        deleteEvent
    };
}
